/**
 * „Per Barcode hinzufügen“: Code scannen oder eintippen, dann zuerst in den
 * eigenen Produkten und danach in Open Food Facts suchen. Ein Treffer lässt
 * sich prüfen und übernehmen; auf der Einkaufsliste kommt das Produkt danach
 * direkt auf die Liste.
 */
import { isValidEan } from '@cookbook/domain';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, isOffline } from '../../api/client.ts';
import type { EanLookup, Product } from '../../api/types.ts';
import { texts } from '../../texts/de.ts';
import { AppIcon } from '../AppIcon.tsx';
import { BarcodeScanner } from './BarcodeScanner.tsx';
import { FoundProductCard } from './FoundProductCard.tsx';

interface ScanDialogProps {
  /** Auf der Einkaufsliste: Produkt auf die Liste setzen. Ohne Angabe nur übernehmen. */
  readonly onAddToList?: (product: Product) => void;
  readonly onClose: () => void;
}

/** Zustand nach dem Lesen eines Codes. */
type Lookup =
  | { state: 'idle' }
  | { state: 'loading'; code: string }
  | { state: 'done'; code: string; result: EanLookup }
  | { state: 'error'; code: string; message: string };

/** Dialog zum Scannen. */
export function ScanDialog({ onAddToList, onClose }: ScanDialogProps) {
  const [lookup, setLookup] = useState<Lookup>({ state: 'idle' });
  const [manual, setManual] = useState('');
  const [manualError, setManualError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [done, setDone] = useState<{ product: Product; warnings: readonly string[] } | null>(null);

  async function search(code: string) {
    setLookup({ state: 'loading', code });
    setDone(null);
    try {
      const result = await api.get<EanLookup>(`/api/sources/ean/${code}`);
      setLookup({ state: 'done', code, result });
    } catch (caught) {
      setLookup({
        state: 'error',
        code,
        message: isOffline(caught)
          ? texts.scan.offline
          : caught instanceof ApiError
            ? caught.message
            : texts.common.error,
      });
    }
  }

  /** Abschluss: auf die Liste setzen oder nur melden. */
  function finish(product: Product, warnings: readonly string[] = []) {
    if (onAddToList !== undefined) {
      onAddToList(product);
    }
    setDone({ product, warnings });
  }

  /** Ohne Treffer: Produkt mit dieser EAN selbst anlegen. */
  async function createOwn(code: string) {
    try {
      const created = await api.post<{ product: Product }>('/api/products', { name: newName });
      const withVariant = await api.post<{ product: Product }>(
        `/api/products/${created.product.id}/variants`,
        {
          ean: code,
        },
      );
      finish(withVariant.product);
    } catch (caught) {
      setLookup({
        state: 'error',
        code,
        message: caught instanceof ApiError ? caught.message : texts.common.error,
      });
    }
  }

  const actionLabel = onAddToList === undefined ? texts.scan.adopt : texts.scan.adoptAndAdd;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="scan-title"
        onClick={(event) => event.stopPropagation()}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-surface-raised p-5 shadow-xl sm:rounded-3xl"
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 id="scan-title" className="flex items-center gap-2 text-lg font-bold">
            <AppIcon name="barcode" size={22} className="text-accent" />
            {texts.scan.title}
          </h2>
          <button type="button" onClick={onClose} className="rounded-lg px-2 py-1 text-sm text-content-muted">
            {texts.common.close}
          </button>
        </div>

        {done !== null ? (
          <div className="space-y-3">
            <p className="rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">
              {(onAddToList === undefined ? texts.scan.adopted : texts.scan.addedToList).replace(
                '{name}',
                done.product.name,
              )}
            </p>
            {done.warnings.map((warning) => (
              <p key={warning} className="text-sm text-content-muted">
                {warning}
              </p>
            ))}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setDone(null);
                  setLookup({ state: 'idle' });
                  setManual('');
                }}
                className="rounded-xl bg-accent px-4 py-2 font-semibold text-on-accent"
              >
                {texts.scan.next}
              </button>
              <Link
                to={`/produkte/${done.product.id}`}
                className="rounded-xl border border-border-subtle px-4 py-2"
              >
                {texts.scan.openProduct}
              </Link>
            </div>
          </div>
        ) : lookup.state === 'idle' ? (
          <div className="space-y-3">
            <BarcodeScanner onCode={(code) => void search(code)} />
            <form
              className="flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                const code = manual.replace(/\D/g, '');
                if (!isValidEan(code)) {
                  setManualError(texts.scan.invalidCode);
                  return;
                }
                setManualError(null);
                void search(code);
              }}
            >
              <input
                value={manual}
                onChange={(event) => setManual(event.target.value)}
                inputMode="numeric"
                placeholder={texts.scan.manualPlaceholder}
                aria-label={texts.scan.manual}
                className="flex-1 rounded-xl border border-border-subtle bg-surface px-3 py-2"
              />
              <button type="submit" className="rounded-xl border border-border-subtle px-4 py-2 text-sm">
                {texts.scan.searchCode}
              </button>
            </form>
            {manualError === null ? null : <p className="text-sm text-accent-strong">{manualError}</p>}
          </div>
        ) : lookup.state === 'loading' ? (
          <p className="text-sm text-content-muted">{texts.scan.searching.replace('{code}', lookup.code)}</p>
        ) : lookup.state === 'error' ? (
          <div className="space-y-3">
            <p className="rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">{lookup.message}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void search(lookup.code)}
                className="rounded-xl border border-border-subtle px-4 py-2 text-sm"
              >
                {texts.common.retry}
              </button>
              <button
                type="button"
                onClick={() => setLookup({ state: 'idle' })}
                className="rounded-xl border border-border-subtle px-4 py-2 text-sm"
              >
                {texts.scan.scanAgain}
              </button>
            </div>
          </div>
        ) : lookup.result.local !== null ? (
          <div className="space-y-3">
            <p className="text-sm">{texts.scan.known.replace('{name}', lookup.result.local.name)}</p>
            {onAddToList === undefined ? (
              <Link
                to={`/produkte/${lookup.result.local.id}`}
                className="block w-full rounded-xl bg-accent px-4 py-2.5 text-center font-semibold text-on-accent"
              >
                {texts.scan.openProduct}
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => lookup.result.local !== null && finish(lookup.result.local)}
                className="w-full rounded-xl bg-accent px-4 py-2.5 font-semibold text-on-accent"
              >
                {texts.scan.addToList}
              </button>
            )}
          </div>
        ) : lookup.result.found[0] !== undefined ? (
          <FoundProductCard
            found={lookup.result.found[0]}
            actionLabel={actionLabel}
            onAdopted={(product, warnings) => finish(product, warnings)}
          />
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-content-muted">
              {(lookup.result.enabled ? texts.scan.notFound : texts.scan.disabled).replace(
                '{code}',
                lookup.code,
              )}
            </p>
            <form
              className="flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                void createOwn(lookup.code);
              }}
            >
              <input
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder={texts.scan.productName}
                aria-label={texts.scan.productName}
                className="flex-1 rounded-xl border border-border-subtle bg-surface px-3 py-2"
              />
              <button
                type="submit"
                disabled={newName.trim() === ''}
                className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-50"
              >
                {texts.scan.createOwn}
              </button>
            </form>
            <button
              type="button"
              onClick={() => setLookup({ state: 'idle' })}
              className="text-sm text-content-muted underline"
            >
              {texts.scan.scanAgain}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
